import { createSelector } from '@reduxjs/toolkit';

import {
  FAILED,
  NO_AUDIO,
  SHORT_DURATION,
  TRANSCRIPTION_STATUSES,
} from 'constants/videoTranscript';
import {
  TRANSCRIPT_PANEL_MODE_CORRECTION,
  TRANSCRIPT_PANEL_MODE_VIEW,
} from 'reducers/shareVideo';

import { selectShareVideo, selectUserIsOwner } from './shareVideo';
import { selectVideo, selectVideoPlayer } from './videoPlayer';

const selectTranscription = state => state.transcription;

export const selectTranscript = createSelector(
  selectTranscription,
  transcription => transcription.transcript
);

export const selectTranscriptSearch = createSelector(
  selectShareVideo,
  shareVideo => shareVideo.transcriptSearch
);

export const selectIsTranscriptSearchOpen = createSelector(
  selectTranscriptSearch,
  transcriptSearch => transcriptSearch.isTranscriptSearchOpen
);

export const selectTranscriptSearchPhrase = createSelector(
  selectTranscriptSearch,
  transcriptSearch => transcriptSearch.transcriptSearchPhrase
);

export const selectTranscriptSearchInputValue = createSelector(
  selectTranscriptSearch,
  transcriptSearch => transcriptSearch.transcriptSearchInputValue
);

export const selectTranscriptSearchPhraseIndexArray = createSelector(
  selectTranscriptSearch,
  transcriptSearch => transcriptSearch.transcriptSearchPhraseIndexArray
);

export const selectCurrentSearchIndex = createSelector(
  selectTranscriptSearch,
  transcriptSearch => transcriptSearch.currentSearchIndex
);

export const selectSelectedElementSource = createSelector(
  selectTranscription,
  transcription => transcription.selectedElementSource
);

export const selectEditedRowIndex = createSelector(
  selectTranscription,
  transcription => transcription.editedRowIndex
);

export const selectHoveredRowIndex = createSelector(
  selectTranscription,
  transcription => transcription.hoveredRowIndex
);

export const selectTranscriptPanelMode = createSelector(
  selectShareVideo,
  shareVideo => shareVideo.transcriptPanelMode
);

export const selectIsTranscriptCorrectionsPopUpOpen = createSelector(
  selectTranscription,
  transcription => transcription.isPopupOpen
);

export const selectTranscriptTextToCorrect = createSelector(
  selectTranscription,
  transcription => transcription.textToCorrect
);

export const selectTranscriptSelectedOccurrenceIndex = createSelector(
  selectTranscription,
  transcription => transcription.selectedOccurrenceIndex
);

export const selectShowDefaultTranscriptHeader = createSelector(
  selectIsTranscriptSearchOpen,
  selectTranscriptPanelMode,
  (isSearchOpen, panelMode) =>
    !isSearchOpen && panelMode === TRANSCRIPT_PANEL_MODE_VIEW
);

export const selectShowTranscriptCorrectionHeader = createSelector(
  selectIsTranscriptSearchOpen,
  selectTranscriptPanelMode,
  selectUserIsOwner,
  (isSearchOpen, panelMode, userIsOwner) =>
    !isSearchOpen &&
    panelMode === TRANSCRIPT_PANEL_MODE_CORRECTION &&
    userIsOwner
);

export const selectUserCanAccessTranscript = createSelector(
  selectVideoPlayer,
  video => video.userCanAccessTranscript
);

export const selectTranscriptDetails = createSelector(
  selectShareVideo,
  shareVideo => shareVideo.transcript_details
);

export const selectTranscriptPhrases = selectTranscript;

export const selectTranscriptWithLocalCorrections = createSelector(
  selectTranscription,
  transcription => transcription.transcriptWithLocalCorrections
);

export const selectTranscriptCorrectionMessage = createSelector(
  selectTranscription,
  transcription => transcription.transcriptCorrectionMessage
);

export const selectTranscriptCorrectionsEnabled = createSelector(
  selectTranscription,
  transcription => transcription.correctionsEnabled
);

export const selectTranscriptionStatus = createSelector(
  selectTranscriptDetails,
  transcriptDetails => transcriptDetails.transcription_status
);

export const selectActiveTranscriptId = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.video?.active_video_transcript_id
);

export const selectShowTranscriptButton = createSelector(
  [
    selectActiveTranscriptId,
    selectUserCanAccessTranscript,
    selectTranscriptionStatus,
  ],
  (activeTranscriptId, userCanAccessTranscript, transcriptStatus) =>
    activeTranscriptId &&
    userCanAccessTranscript &&
    TRANSCRIPTION_STATUSES.includes(transcriptStatus) &&
    transcriptStatus !== FAILED
);

export const selectVideoWillHaveTranscription = createSelector(
  selectTranscriptionStatus,
  status => status && ![NO_AUDIO, SHORT_DURATION, FAILED].includes(status)
);

export const selectShowTranscriptToViewer = createSelector(
  selectVideo,
  video => video.show_transcript_to_viewer
);

export const selectFillerWordRanges = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.fillerWords?.ranges
);

export const selectNumFillerWordsInVideoVersion = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.fillerWords?.numFillerWordsInVideoVersion
);

export const selectFillerWordHighlight = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.fillerWordHighlight
);

export const selectFillerWordRemoval = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.fillerWordRemoval
);

export const selectIsFillerRemovalFTUX = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.isFillerRemovalFTUX
);

export const selectIsTrimmingFillerWords = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.isTrimmingFillerWords
);
